/**
 * AudioManagerProvider Component
 *
 * Wrapper component that initializes the audio manager at app root level.
 * This component must be placed inside BrowserRouter since useAudioManager
 * depends on useLocation from react-router-dom.
 *
 * Usage:
 * ```tsx
 * <BrowserRouter>
 *   <AudioManagerProvider>
 *     <Router />
 *   </AudioManagerProvider>
 * </BrowserRouter>
 * ```
 */

import type { ReactNode } from 'react';
import { useAudioManager } from '@/shared/hooks/useAudioManager';

interface AudioManagerProviderProps {
  children: ReactNode;
}

/**
 * Provider component that initializes audio management for the entire app.
 * The useAudioManager hook handles:
 * - AudioService initialization
 * - Document unlock listener for mobile browsers
 * - Route-based music switching
 * - Settings store subscription for real-time toggle response
 */
export function AudioManagerProvider({ children }: AudioManagerProviderProps) {
  // Initialize audio manager at app root level
  // This ensures the hook is only called once
  useAudioManager();

  // Render children directly - no context needed since AudioService is a singleton
  return <>{children}</>;
}
